import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface TimeSlotSelectorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  minTime?: string; // Start time for circular range generation
  maxTime?: string; // Used with excludeBeforeMin to define first period range
  excludeBeforeMin?: boolean; // If true, excludes first period range (maxTime to minTime)
  excludeBefore?: string; // NEW: Exclude all times before this time (for cross-day validation)
}

// Helper to convert HH:mm to minutes since midnight
const timeToMinutes = (time: string): number => {
  if (!time) return -1;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return -1;
  return h * 60 + m;
};

/**
 * TimeSlotSelector Component
 *
 * A dropdown-based time selector with 15-minute intervals in 24-hour format (HH:mm).
 * Supports circular time ranges for overnight periods and first period exclusion.
 *
 * @param label - Optional label for the time selector
 * @param value - Time value in 24-hour format (HH:mm)
 * @param onChange - Callback function when time changes
 * @param disabled - Whether the selector is disabled
 * @param id - Optional ID for the component
 * @param minTime - Start time for circular range generation (HH:mm)
 * @param maxTime - Used with excludeBeforeMin to exclude first period range
 * @param excludeBeforeMin - If true, excludes times in first period range (maxTime to minTime)
 */
export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  id,
  minTime,
  maxTime,
  excludeBeforeMin = false,
  excludeBefore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Generate all possible 15-minute time slots (00:00 to 23:45)
  const allTimeSlots = React.useMemo(() => {
    const slots: Array<{ value: string; label: string }> = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const value24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const period = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const label12 = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
        slots.push({ value: value24, label: label12 });
      }
    }
    return slots;
  }, []);

  // Generate time slots with circular 24-hour range
  const filteredSlots = React.useMemo(() => {
    console.log('=== SLOT FILTERING ===');
    console.log('🏷️  CONTEXT:', id || 'Unknown');
    console.log('Label:', label);
    console.log('excludeBefore:', excludeBefore);
    console.log('minTime:', minTime);
    console.log('maxTime:', maxTime);
    console.log('excludeBeforeMin:', excludeBeforeMin);
    
    const minMinutes = minTime ? timeToMinutes(minTime) : -1;
    const maxMinutes = maxTime ? timeToMinutes(maxTime) : -1;
    const excludeBeforeMinutes = excludeBefore ? timeToMinutes(excludeBefore) : -1;

    console.log('minMinutes:', minMinutes);
    console.log('maxMinutes:', maxMinutes);
    console.log('excludeBeforeMinutes:', excludeBeforeMinutes);

    // Special case: Second Period End when Start is empty but First Period exists
    // This handles the case where First Period is 24-hour operation
    if (excludeBeforeMin && minMinutes === -1 && maxMinutes !== -1) {
      console.log('❌ No slots available - 24-hour first period');
      console.log('======================\n');
      return []; // No slots available - cannot have second period with 24-hour first period
    }

    // NEW: Apply excludeBefore filter for cross-day validation
    if (excludeBeforeMinutes !== -1 && minMinutes === -1) {
      console.log('✅ Applying excludeBefore filter (cross-day validation)');
      const filteredByExcludeBefore = allTimeSlots.filter(slot => {
        const slotMinutes = timeToMinutes(slot.value);
        return slotMinutes >= excludeBeforeMinutes;
      });
      console.log('Total slots before filtering:', allTimeSlots.length);
      console.log('Total slots after excludeBefore:', filteredByExcludeBefore.length);
      console.log('First available slot:', filteredByExcludeBefore[0]?.label || 'None');
      console.log('📋 ALL AVAILABLE SLOTS:', filteredByExcludeBefore.map(s => s.label).join(', '));
      console.log('======================\n');
      
      if (searchQuery) {
        return filteredByExcludeBefore.filter((slot) =>
          slot.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      return filteredByExcludeBefore;
    }

    // Generate circular 24-hour range starting from minTime
    if (minMinutes !== -1) {
      const circularSlots: Array<{ value: string; label: string }> = [];
      
      // Generate 96 slots (24 hours * 4 fifteen-minute intervals) starting AFTER minTime
      for (let i = 1; i <= 96; i++) {
        const totalMinutes = (minMinutes + (i * 15)) % 1440; // Wrap at 1440 (24 hours)
        
        // Apply strict filtering ONLY if excludeBeforeMin is true (for Second Period Start)
        if (excludeBeforeMin && maxMinutes !== -1) {
          // 24-hour operation: No second period possible
          if (maxMinutes === minMinutes) {
            continue; // Skip all times when first period is 24-hour operation
          }
          
          // When maxTime is provided, exclude the first period range
          const isOvernight = maxMinutes > minMinutes;
          
          if (isOvernight) {
            // Overnight first period: Exclude times > maxTime OR <= minTime
            if (totalMinutes > maxMinutes || totalMinutes <= minMinutes) {
              continue;
            }
          } else {
            // Normal first period: Exclude times > maxTime AND <= minMinutes
            if (totalMinutes > maxMinutes && totalMinutes <= minMinutes) {
              continue;
            }
          }
        } else if (excludeBeforeMin && totalMinutes === minMinutes) {
          // Fallback: Skip only the exact minTime
          continue;
        }
        
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        
        // 24-hour format for value
        const value24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // 12-hour format for display
        const period = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const label12 = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
        
        circularSlots.push({ value: value24, label: label12 });
      }
      
      // Apply search query filter if needed
      if (searchQuery) {
        return circularSlots.filter((slot) =>
          slot.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      console.log('Total circular slots:', circularSlots.length);
      console.log('First available slot:', circularSlots[0]?.label || 'None');
      console.log('📋 ALL CIRCULAR SLOTS:', circularSlots.map(s => s.label).join(', '));
      console.log('======================\n');
      
      return circularSlots;
    }

    // Fallback: return all slots if no minTime provided
    console.log('Fallback: returning all slots');
    console.log('Total slots:', allTimeSlots.length);
    console.log('📋 ALL SLOTS:', allTimeSlots.map(s => s.label).join(', '));
    console.log('======================\n');
    return allTimeSlots;

  }, [allTimeSlots, searchQuery, minTime, maxTime, value, excludeBeforeMin, excludeBefore]);

  // Update selected time when value prop changes
  useEffect(() => {
    if (value) {
      setSelectedTime(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleTimeSelect = (timeValue: string) => {
    setSelectedTime(timeValue);
    onChange(timeValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const getDisplayLabel = () => {
    if (!selectedTime) return 'Select time';
    const slot = allTimeSlots.find((s) => s.value === selectedTime);
    return slot ? slot.label : 'Select time';
  };

  const componentId = id || `time-slot-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="flex-1 relative" ref={dropdownRef}>
      {label && (
        <label htmlFor={componentId} className="block text-xs font-medium text-[var(--text-primary)] mb-1">
          {label}
        </label>
      )}

      {/* Custom Dropdown Button */}
      <button
        id={componentId}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-2 py-2 text-sm border rounded flex items-center justify-between ${
          disabled
            ? 'bg-gray-100 cursor-not-allowed text-gray-500'
            : 'bg-white text-[var(--text-primary)] hover:border-[var(--accent-color)]'
        } border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent transition-colors`}
      >
        <span className={selectedTime ? '' : 'text-[var(--text-muted)]'}>
          {getDisplayLabel()}
        </span>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4 text-[var(--text-secondary)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>

      {/* Animated Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute z-50 w-full mt-1 bg-white border border-[var(--border-color)] rounded-lg shadow-lg overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-[var(--border-color)]">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search time..."
                className="w-full px-2 py-1.5 text-sm border border-[var(--border-color)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
              />
            </div>

            {/* Time Slots List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredSlots.length > 0 ? (
                filteredSlots.map((slot, index) => (
                  <motion.button
                    key={slot.value}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.01, duration: 0.15 }}
                    type="button"
                    onClick={() => handleTimeSelect(slot.value)}
                    className={`w-full px-3 py-2 text-sm text-left hover:bg-[var(--bg-hover)] transition-colors ${
                      selectedTime === slot.value
                        ? 'bg-[var(--accent-color)] text-white hover:bg-[var(--accent-color)]/90'
                        : 'text-[var(--text-primary)]'
                    }`}
                  >
                    {slot.label}
                  </motion.button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-center text-[var(--text-secondary)]">
                  No times found
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
